"use client";

import { newsHref } from "@/lib/news-image";
import { useTranslations } from 'next-intl';
import Image from "next/image";
import { PerRowPicker, PER_ROW_GRID, usePerRow } from '@/components/ui/per-row-picker';
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage, getInitials } from "@/components/ui/avatar";
import {
  ArrowLeft, Globe, Music2, Users, UserRound, Calendar, MapPin, Play, Pause, ExternalLink,
  Disc, Heart, MessageCircle, Video, Newspaper, ShoppingBag,
  ChevronDown, ChevronUp, Loader2, PlusCircle, Upload, X, Pencil, Check,
  AlertTriangle as AlertTriangleIcon, Award,
} from "lucide-react";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input as UIInput } from "@/components/ui/input";
import { Label as UILabel } from "@/components/ui/label";
import {
  Select as UISelect, SelectContent as UISelectContent,
  SelectItem as UISelectItem, SelectTrigger as UISelectTrigger, SelectValue as UISelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { addDoc, collection as fbCollection } from "firebase/firestore";
import { ShareButton } from "@/components/ui/share-button";
import { QRShareButton } from "@/components/ui/qr-share-button";
import { MediaGalleryGrid } from "@/components/media/media-gallery-grid";
import React, { useState, useEffect, useCallback, use } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getArtistBySlug, getArtistById } from "@/services/artists";
import type { ArtistProfile } from "@/services/artists";
import { db } from "@/lib/firebase";
import { collection, getDocs, getDoc, query, where, orderBy, limit, doc, updateDoc, deleteField } from "firebase/firestore";
import { getAgreementsForUser } from "@/services/split-agreements";
import { normGenreList, pickSimilar } from "@/lib/similar-content";
import { useMediaPlayer } from "@/contexts/media-player-context";
import { useAuthGuard } from "@/hooks/use-require-auth";
import { getOrCreateDMRoom, notifyArtistNewFollower } from "@/services/chat";
import { useAdmin } from "@/hooks/use-admin";
import { toggleFollow, isFollowing } from "@/services/follows";
import SaveButton from "@/components/save-button";
import WebResultsSection from "@/components/web-results-section";
import { SocialEmbeds } from "@/components/social/social-embeds";
import { LinkedAccounts } from "@/components/social/linked-accounts";
import { normalizeSocialLinks, type LinkedAccount } from "@/lib/social-platforms";
import { getEmbedUrl, extractVideoId, getThumbnailUrl } from "@/lib/youtube";
import AdminToolbar from "@/components/admin/admin-toolbar";
import { updateArtist, deleteArtist, toggleAdminHidden } from "@/services/artists";
import { useRouter } from "next/navigation";
import { FollowConfirmPopup, type FollowConfirmData } from "@/components/social/follow-confirm-popup";
import { DownloadButton } from "@/components/media/download-button";
import { TiltCard, CoverExplicitSticker } from "@/components/media/tilt-card";
import { trackMediaEvent } from "@/services/stats";
import { MusicGroupLD } from "@/components/seo/json-ld";
import { GenerativeArtistAvatar } from "@/components/media/generative-artist-avatar";
import { LinkedSessionBadge } from "@/components/artwork/linked-session-badge";
import { SponsorButton } from "@/components/payment/sponsor-button";
import { GenerativeAvatarCustomizer } from "@/components/media/generative-avatar-customizer";
import { ArtistProfileEditDialog } from "@/components/admin/artist-profile-edit-dialog";

// Social media icons
const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const YoutubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"/>
    <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="white"/>
  </svg>
);

const SpotifyIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

const SoundCloudIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
    <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.05-.1-.1-.1m-.899.828c-.06 0-.091.037-.104.094L0 14.479l.172 1.282c.013.06.045.094.104.094.06 0 .09-.037.104-.094l.193-1.282-.206-1.332c-.014-.057-.044-.094-.104-.094m1.8-.773c-.066 0-.12.046-.12.12l-.209 2.079.209 2.07c0 .074.054.12.12.12.066 0 .12-.046.12-.12l.227-2.07-.227-2.079c0-.074-.054-.12-.12-.12m.898-.463c-.066 0-.12.042-.127.114l-.195 2.542.195 2.474c.007.072.061.114.127.114.066 0 .12-.042.127-.114l.209-2.474-.209-2.542c-.007-.072-.061-.114-.127-.114m.9-.263c-.074 0-.135.049-.135.135l-.175 2.805.175 2.691c0 .086.061.135.135.135.074 0 .135-.049.135-.135l.195-2.691-.195-2.805c0-.086-.061-.135-.135-.135m.899-.165c-.082 0-.15.055-.15.15l-.158 2.97.158 2.838c0 .095.068.15.15.15.082 0 .15-.055.15-.15l.172-2.838-.172-2.97c0-.095-.068-.15-.15-.15m.93-.178c-.09 0-.163.065-.163.165l-.137 3.148.137 2.912c0 .1.073.165.163.165s.163-.065.163-.165l.15-2.912-.15-3.148c0-.1-.073-.165-.163-.165m.93 0c-.098 0-.18.07-.18.18l-.12 3.148.12 2.888c0 .11.082.18.18.18s.18-.07.18-.18l.13-2.888-.13-3.148c0-.11-.082-.18-.18-.18m.962-.098c-.106 0-.195.08-.195.195l-.1 3.246.1 2.865c0 .115.089.195.195.195s.195-.08.195-.195l.11-2.865-.11-3.246c0-.115-.089-.195-.195-.195m.953-.085c-.114 0-.203.09-.21.21l-.083 3.331.083 2.832c.007.12.096.21.21.21.114 0 .203-.09.21-.21l.09-2.832-.09-3.331c-.007-.12-.096-.21-.21-.21m.988 0c-.12 0-.221.095-.221.225l-.061 3.331.061 2.82c0 .13.101.225.221.225.12 0 .221-.095.221-.225l.068-2.82-.068-3.331c0-.13-.101-.225-.221-.225m1.015-.053c-.13 0-.233.105-.24.24l-.04 3.384.04 2.797c.007.135.11.24.24.24.13 0 .233-.105.24-.24l.046-2.797-.046-3.384c-.007-.135-.11-.24-.24-.24m1.008-.04c-.135 0-.248.11-.248.255l-.022 3.424.022 2.773c0 .145.113.255.248.255.135 0 .248-.11.248-.255l.027-2.773-.027-3.424c0-.145-.113-.255-.248-.255m1.06-.054c-.15 0-.27.12-.27.27v.003l-.013 3.475.013 2.761c0 .15.12.27.27.27.15 0 .27-.12.27-.27l.014-2.761-.014-3.478c0-.15-.12-.27-.27-.27m1.073-.032c-.156 0-.285.126-.285.285l.002 3.507.002 2.738c0 .159.129.285.285.285.156 0 .285-.126.285-.285l-.002-2.738.002-3.507c0-.159-.129-.285-.285-.285m1.053-.16c-.158 0-.293.135-.293.3l-.009 3.667.009 2.697c0 .165.135.3.293.3.159 0 .293-.135.293-.3l-.002-2.697.009-3.667c0-.165-.135-.3-.293-.3"/>
    <path d="M19.08 9.04c-.505 0-.99.09-1.44.253-.3-3.36-3.103-5.993-6.53-5.993-1.003 0-1.975.24-2.817.64-.33.157-.42.32-.42.633v10.62c0 .33.266.6.586.633h10.621c1.987 0 3.6-1.613 3.6-3.6 0-1.987-1.613-3.187-3.6-3.187z"/>
  </svg>
);

const BandcampIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
    <path d="M0 18.75l7.437-13.5H24l-7.438 13.5H0z"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.847h-7.407l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.153h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

const DiscogsIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="12" r="3" fill="currentColor"/>
    <circle cx="12" cy="12" r="6.5" fill="none" stroke="currentColor" strokeWidth="0.8"/>
  </svg>
);

const socialPlatformConfig: Record<string, { icon: React.FC; label: string; color: string }> = {
  instagram: { icon: InstagramIcon, label: "Instagram", color: "hover:text-pink-500" },
  youtube: { icon: YoutubeIcon, label: "YouTube", color: "hover:text-red-500" },
  spotify: { icon: SpotifyIcon, label: "Spotify", color: "hover:text-green-500" },
  soundcloud: { icon: SoundCloudIcon, label: "SoundCloud", color: "hover:text-orange-500" },
  bandcamp: { icon: BandcampIcon, label: "Bandcamp", color: "hover:text-cyan-500" },
  twitter: { icon: XIcon, label: "X / Twitter", color: "hover:text-foreground" },
  x: { icon: XIcon, label: "X / Twitter", color: "hover:text-foreground" },
  facebook: { icon: FacebookIcon, label: "Facebook", color: "hover:text-blue-500" },
  tiktok: { icon: TikTokIcon, label: "TikTok", color: "hover:text-foreground" },
  discogs: { icon: DiscogsIcon, label: "Discogs", color: "hover:text-foreground" },
  website: { icon: () => <Globe className="h-5 w-5" />, label: "Website", color: "hover:text-primary" },
};

export default function ArtistPage({ params }: { params: Promise<{ slug: string }> }) {
  const resolvedParams = use(params);
  const t = useTranslations('artists');
  const tCommon = useTranslations('common');
  const tMusic = useTranslations('music');
  const tProfile = useTranslations('profile');
  const tAdmin = useTranslations('admin');
  const [artist, setArtist] = useState<ArtistProfile | null>(null);
  const [avatarPattern, setAvatarPattern] = useState<number | undefined>(undefined);
  const [avatarPalette, setAvatarPalette] = useState<number | undefined>(undefined);
  /** New customizer fields: bg + line colours. `undefined` = avatar defaults
   *  (transparent bg, theme-foreground lines). */
  const [avatarBg, setAvatarBg] = useState<string | undefined>(undefined);
  const [avatarFg, setAvatarFg] = useState<string | undefined>(undefined);
  const [avatarFont, setAvatarFont] = useState<string | undefined>(undefined);
  const [releases, setReleases] = useState<any[]>([]);
  // Releases where this artist is a FEATURED artist (not the primary), or that
  // belong to a band/group this artist is a listed member of — never this
  // artist's own primary releases (those stay in `releases` above).
  const [featuredReleases, setFeaturedReleases] = useState<any[]>([]);
  // Genre-matched artists — "You might also like", shown below the main content.
  const [similarArtists, setSimilarArtists] = useState<any[]>([]);
  const [similarPerRow, setSimilarPerRow] = usePerRow('bb-similar-per-row');
  const [works, setWorks] = useState<any[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [news, setNews] = useState<any[]>([]);
  const [shopItems, setShopItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFullBio, setShowFullBio] = useState(false);
  const [isFollowingArtist, setIsFollowingArtist] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followPopupData, setFollowPopupData] = useState<FollowConfirmData | null>(null);
  const { playTrack, playTracks, isPlaying, currentTrack, togglePlay, isActive: isPlayerActive, analyserL } = useMediaPlayer();
  const { user, userProfile, guardAction } = useAuthGuard();
  const { isAdmin, isSuperAdmin } = useAdmin();
  const router = useRouter();
  const { toast } = useToast();

  // Is the signed-in user a connected owner/manager of this artist? (artist-link.ts)
  const isOwner = !!user?.uid && (
    (artist as any)?.ownerUid === user.uid ||
    ((artist as any)?.connectedUserIds || []).includes(user.uid)
  );

  // The linked owner account's own streaming/social links (Profile -> Social).
  // Visitors can't read users/*, so the public userProfiles/{uid} mirror that
  // Social Hub writes is read first; signed-in viewers also get users/{uid}
  // (covers links saved before the mirror existed). Only the PRIMARY owner —
  // never createdBy (an admin who set the page up) or every connected manager.
  const linkedOwnerUid: string | undefined =
    (artist as any)?.ownerUid || (artist as any)?.linkedUserId || (artist as any)?.userId ||
    ((artist as any)?.connectedUserIds || [])[0] || undefined;
  const signedIn = !!user;
  const [ownerLinks, setOwnerLinks] = useState<LinkedAccount[]>([]);
  useEffect(() => {
    if (!linkedOwnerUid) { setOwnerLinks([]); return; }
    let cancelled = false;
    Promise.all([
      getDoc(doc(db, "userProfiles", linkedOwnerUid)).then(snap => snap.data()?.socialLinks).catch(() => null),
      signedIn ? getDoc(doc(db, "users", linkedOwnerUid)).then(snap => snap.data()?.socialLinks).catch(() => null) : Promise.resolve(null),
    ]).then(([mirror, own]) => {
      if (!cancelled) setOwnerLinks(normalizeSocialLinks(mirror, own));
    });
    return () => { cancelled = true; };
  }, [linkedOwnerUid, signedIn]);

  // Send a DM to this artist (requires artist to have a linked userId)
  const handleMessage = guardAction(async () => {
    const artistUserId = (artist as any)?.userId || (artist as any)?.createdByUid || (artist as any)?.createdBy;
    if (!artistUserId || !user) {
      toast({ title: 'Cannot message', description: 'This artist is not linked to a platform account.', variant: 'destructive' });
      return;
    }
    try {
      const room = await getOrCreateDMRoom(
        user.uid, userProfile?.displayName || 'Me', userProfile?.profileImageUrl || '',
        artistUserId, artist?.name || 'Artist', artist?.imageUrl || ''
      );
      window.dispatchEvent(new CustomEvent('open-chat-window', { detail: { roomId: room.id } }));
    } catch {
      toast({ title: 'Error', description: 'Could not open chat. Please try again.', variant: 'destructive' });
    }
  });

  // Edit Profile dialog state
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  // Add Content dialog state
  const [addReleaseOpen, setAddReleaseOpen] = useState(false);
  const [newReleaseTitle, setNewReleaseTitle] = useState("");
  const [newReleaseType, setNewReleaseType] = useState("Album");
  const [newReleaseCoverUrl, setNewReleaseCoverUrl] = useState("");
  const [isSavingRelease, setIsSavingRelease] = useState(false);

  // Attach-existing dialog (link existing works/releases/videos to this artist)
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachTab, setAttachTab] = useState<"track" | "release" | "video">("track");
  const [attachSearch, setAttachSearch] = useState("");
  const [attachCandidates, setAttachCandidates] = useState<any[]>([]);
  const [attachLoading, setAttachLoading] = useState(false);
  const [attachBusyId, setAttachBusyId] = useState("");

  useEffect(() => {
    async function loadArtist() {
      try {
        let data = await getArtistBySlug(resolvedParams.slug);
        if (!data) {
          data = await getArtistById(resolvedParams.slug);
        }
        setArtist(data);
        if (data) {
          // Load avatar config overrides (if creator has customised the generative art)
          const cfg = (data as any).avatarConfig;
          if (cfg) {
            // New shape: { patternIndex, bgColor, fgColor }. Legacy shape
            // `patternType` / `paletteIndex` still honoured so existing docs
            // saved before today's rebuild keep working.
            const pIdx = cfg.patternIndex ?? cfg.patternType;
            if (pIdx !== null && pIdx !== undefined) setAvatarPattern(pIdx);
            if (cfg.paletteIndex !== null && cfg.paletteIndex !== undefined) setAvatarPalette(cfg.paletteIndex);
            if (cfg.bgColor) setAvatarBg(cfg.bgColor);
            if (cfg.fgColor) setAvatarFg(cfg.fgColor);
            if (cfg.fontFamily) {
              setAvatarFont(cfg.fontFamily);
              import('@/lib/fonts').then(m => m.ensureFontLoaded(cfg.fontFamily)).catch(() => {});
            }
          }
          trackMediaEvent({
            type: 'view',
            entityType: 'artist',
            entityId: data.id || resolvedParams.slug,
            entityTitle: data.name,
          });
        }

        if (data) {
          const artistName = data.name.trim().toLowerCase();

          // Load releases — query by artistId (DB-level), then name-match fallback
          try {
            const [relByIdSnap, relByNameSnap] = await Promise.all([
              getDocs(query(collection(db, "releases"), where("artistId", "==", data!.id), limit(100))),
              getDocs(query(collection(db, "releases"), limit(100))),
            ]);
            const seenRelIds = new Set<string>();
            const matched: any[] = [];
            relByIdSnap.docs.forEach(d => {
              seenRelIds.add(d.id);
              matched.push({ id: d.id, ...d.data() });
            });
            relByNameSnap.docs.forEach(d => {
              if (seenRelIds.has(d.id)) return;
              const rd = d.data();
              const nameMatch =
                rd.artist?.docId === data!.id ||
                rd.artist?.name?.trim().toLowerCase() === artistName ||
                rd.artistName?.trim().toLowerCase() === artistName ||
                (typeof rd.artist === 'string' && rd.artist.trim().toLowerCase() === artistName);
              if (nameMatch) {
                seenRelIds.add(d.id);
                matched.push({ id: d.id, ...rd });
              }
            });
            matched.sort((a: any, b: any) => {
              const da = a.releaseDate?.toDate ? a.releaseDate.toDate() : new Date(a.releaseDate || 0);
              const db2 = b.releaseDate?.toDate ? b.releaseDate.toDate() : new Date(b.releaseDate || 0);
              return db2.getTime() - da.getTime();
            });
            // Only hide releases that an admin explicitly marked as
            // `incomplete: true` via Super Admin → AWAL Import → Hide
            // audio-less. The implicit "every embedded track has audioUrl"
            // check was wrongly hiding healthy releases whose audio lives
            // on each work doc rather than embedded in release.tracks[].
            const canSeeBroken = isAdmin || isSuperAdmin;
            const visible = (canSeeBroken
              ? matched
              : matched.filter(r => r.incomplete !== true))
              // Admin-hidden / hidden releases never show on the public artist page
              // (Gordon hid "Sketches" in Super Admin but it still appeared here).
              .filter((r: any) => !r.adminHidden && !r.hidden);
            setReleases(visible);

            // ── Featured-on + band/group releases ──────────────────────────
            // Reuses the `relByNameSnap` scan already fetched above (no extra
            // query) to find releases where this artist appears in
            // featuredArtists[] rather than as the primary artist. Then does a
            // separate pass over `artists` to find bands/groups this artist is
            // a listed member of, and pulls in THEIR releases too.
            try {
              const seenFeatIds = new Set<string>(); // dedup across featured + band matches
              const feat: any[] = [];

              relByNameSnap.docs.forEach(d => {
                if (seenRelIds.has(d.id) || seenFeatIds.has(d.id)) return;
                const rd = d.data();
                const isFeatured = Array.isArray(rd.featuredArtists) &&
                  rd.featuredArtists.some((fa: string) => (fa || '').trim().toLowerCase() === artistName);
                if (isFeatured) {
                  seenFeatIds.add(d.id);
                  feat.push({ id: d.id, ...rd, _relation: 'featured' });
                }
              });

              // Bands/groups this artist is a member of: scan artists for a
              // members[] entry matching this artist's name (members are
              // stored as free-text {name, role}, not linked doc ids).
              const artistsSnap = await getDocs(query(collection(db, "artists"), limit(300)));
              const bands = artistsSnap.docs.filter(ad => {
                if (ad.id === data!.id) return false;
                const bd = ad.data();
                return Array.isArray(bd.members) &&
                  bd.members.some((m: any) => (m?.name || '').trim().toLowerCase() === artistName);
              });

              if (bands.length > 0) {
                const bandReleaseLists = await Promise.all(bands.map(async (bandDoc) => {
                  const bandName = bandDoc.data().name || 'Band';
                  const [byId, byNameAll] = await Promise.all([
                    getDocs(query(collection(db, "releases"), where("artistId", "==", bandDoc.id), limit(50))),
                    getDocs(query(collection(db, "releases"), limit(100))),
                  ]);
                  const list: any[] = [];
                  byId.docs.forEach(d => list.push({ id: d.id, ...d.data(), _bandName: bandName }));
                  const bandNameLc = bandName.trim().toLowerCase();
                  byNameAll.docs.forEach(d => {
                    if (list.some(r => r.id === d.id)) return;
                    const rd = d.data();
                    const match =
                      rd.artist?.docId === bandDoc.id ||
                      rd.artist?.name?.trim().toLowerCase() === bandNameLc ||
                      rd.artistName?.trim().toLowerCase() === bandNameLc ||
                      (typeof rd.artist === 'string' && rd.artist.trim().toLowerCase() === bandNameLc);
                    if (match) list.push({ id: d.id, ...rd, _bandName: bandName });
                  });
                  return list;
                }));
                bandReleaseLists.flat().forEach((r: any) => {
                  if (seenRelIds.has(r.id) || seenFeatIds.has(r.id)) return;
                  if (r.adminHidden || r.hidden) return;
                  seenFeatIds.add(r.id);
                  feat.push({ ...r, _relation: 'band' });
                });
              }

              feat.sort((a: any, b: any) => {
                const da = a.releaseDate?.toDate ? a.releaseDate.toDate() : new Date(a.releaseDate || 0);
                const db2 = b.releaseDate?.toDate ? b.releaseDate.toDate() : new Date(b.releaseDate || 0);
                return db2.getTime() - da.getTime();
              });
              setFeaturedReleases(feat);

              // ── Similar artists ── genre-overlap scoring over the same
              // artistsSnap scan (no extra query) — "You might also like".
              const myGenres = normGenreList((data as any).genres, (data as any).genre);
              const similarCandidates = artistsSnap.docs
                .filter(ad => ad.id !== data!.id)
                .map(ad => {
                  const bd = ad.data() as any;
                  return { id: ad.id, ...bd, genres: normGenreList(bd.genres, bd.genre) };
                })
                .filter((a: any) => !a.adminHidden);
              setSimilarArtists(pickSimilar(similarCandidates, myGenres, new Set([data!.id]), 10));
            } catch { /* non-fatal — main releases already loaded */ }
          } catch { }

          // Load works/tracks — query by artistId first (DB-level, no limit issue),
          // then fall back to name matching for older records without artistId.
          try {
            const [byIdSnap, byNameSnap] = await Promise.all([
              getDocs(query(collection(db, "works"), where("artistId", "==", data!.id), limit(200))),
              getDocs(query(collection(db, "works"), limit(200))),
            ]);
            const seenWorkIds = new Set<string>();
            const matched: any[] = [];
            // Primary: works explicitly linked by artistId
            byIdSnap.docs.forEach(d => {
              seenWorkIds.add(d.id);
              matched.push({ id: d.id, ...d.data() });
            });
            // Secondary: name-matched works not already included
            byNameSnap.docs.forEach(d => {
              if (seenWorkIds.has(d.id)) return;
              const wd = d.data();
              const nameMatch =
                wd.artist?.docId === data!.id ||
                wd.artist?.name?.toLowerCase() === artistName ||
                wd.artistName?.toLowerCase() === artistName ||
                (typeof wd.artist === 'string' && wd.artist.trim().toLowerCase() === artistName);
              if (nameMatch) {
                seenWorkIds.add(d.id);
                matched.push({ id: d.id, ...wd });
              }
            });

            // Tertiary: tracks where this artist's linked user account(s) are
            // credited as a Contributor or a Rights & Splits participant, even
            // when someone else is the primary artist. Gordon: "now we have
            // contributors and rights, both need to show up in the profile."
            const linkedUserIds = Array.from(new Set(
              [(data as any).ownerUid, ...((data as any).connectedUserIds || [])].filter(Boolean)
            )) as string[];
            if (linkedUserIds.length > 0) {
              try {
                const [contribSnap, agreementsByUser] = await Promise.all([
                  getDocs(query(collection(db, "works"), where("contributorUserIds", "array-contains-any", linkedUserIds.slice(0, 10)), limit(200))),
                  Promise.all(linkedUserIds.map(uid => getAgreementsForUser(uid).catch(() => []))),
                ]);
                contribSnap.docs.forEach(d => {
                  if (seenWorkIds.has(d.id)) return;
                  seenWorkIds.add(d.id);
                  matched.push({ id: d.id, ...d.data(), _creditedVia: 'contributor' });
                });
                const rightsWorkIds = new Set<string>();
                agreementsByUser.flat().forEach(a => {
                  if (a.mediaType === 'work' && a.mediaId && !seenWorkIds.has(a.mediaId)) rightsWorkIds.add(a.mediaId);
                });
                await Promise.all(Array.from(rightsWorkIds).map(async workId => {
                  try {
                    const wSnap = await getDoc(doc(db, "works", workId));
                    if (wSnap.exists() && !seenWorkIds.has(workId)) {
                      seenWorkIds.add(workId);
                      matched.push({ id: workId, ...wSnap.data(), _creditedVia: 'rights' });
                    }
                  } catch { }
                }));
              } catch { }
            }

            // Hide tracks with no playable audio file. Per Gordon: "hide
            // all tracks without audio files." Video works and YouTube
            // imports pass through. Admins reach the unfiltered list via
            // Super Admin → Media; this page is public-facing.
            const playable = matched.filter(w => {
              if ((w as any).adminHidden || (w as any).hidden) return false; // respect hide toggle
              // Media Vault bulk-edit "Privacy: Unlisted/Private" must also pull
              // a track off the artist page — it didn't before (Gordon
              // 2026-09-26: "I unlisted all these tracks so they should not
              // show up in the demos by Aeon"), since this filter only ever
              // checked adminHidden/hidden, not the work's own privacy field.
              if ((w as any).privacy === 'unlisted' || (w as any).privacy === 'private') return false;
              // Admins see EVERY linked work (even ones without streaming audio)
              // so attached-but-unconverted tracks are visible/manageable here.
              // Playback still filters by audio downstream, so this is safe.
              if (isAdmin || isSuperAdmin) return true;
              if ((w as any).type === 'video' || (w as any).videoUrl || (w as any).youtubeId) return true;
              const audio = (w as any).audioUrl || (w as any).mp3Url || (w as any).hiResUrl || (w as any).sourceUrl || (w as any).fileUrl || '';
              return !!audio;
            });
            setWorks(playable);
          } catch { }

          // Load videos
          try {
            const vidQ = query(collection(db, "videos"), limit(50));
            const vidSnap = await getDocs(vidQ);
            const matched = vidSnap.docs
              .filter(d => {
                const vd = d.data();
                return (
                  vd.artist?.docId === data!.id ||
                  vd.artist?.name?.trim().toLowerCase() === artistName ||
                  vd.artistName?.trim().toLowerCase() === artistName ||
                  (typeof vd.artist === 'string' && vd.artist.trim().toLowerCase() === artistName)
                );
              })
              .map(d => ({ id: d.id, ...d.data() }));
            setVideos(matched);
          } catch { }

          // Load related news
          try {
            const newsQ = query(collection(db, "news"), limit(50));
            const newsSnap = await getDocs(newsQ);
            const matched = newsSnap.docs
              .filter(d => {
                const nd = d.data();
                const content = `${nd.title || ""} ${nd.excerpt || ""} ${nd.content || ""} ${nd.author || ""}`.toLowerCase();
                return content.includes(artistName);
              })
              .map(d => ({ id: d.id, ...d.data() }));
            setNews(matched);
          } catch { }

          // Merch tied to this artist — by artistId (Printful import links it
          // from the product title), artist.docId, or the name. All active
          // items: the old 50-item cap missed most of a big merch drop.
          try {
            const shopQ = query(collection(db, "item"), where("status", "==", "active"), limit(1000));
            const shopSnap = await getDocs(shopQ);
            const matched = shopSnap.docs
              .filter(d => {
                const sd = d.data();
                if (sd.adminHidden) return false;
                if (sd.artistId === data!.id || sd.artist?.docId === data!.id) return true;
                const content = `${sd.title || ""} ${sd.description || ""} ${sd.artist?.name || ""}`.toLowerCase();
                return content.includes(artistName);
              })
              .map(d => ({ id: d.id, ...d.data() }));
            setShopItems(matched);
          } catch { }
        }
      } catch (err) {
        console.error("Error loading artist:", err);
      } finally {
        setIsLoading(false);
      }
    }
    loadArtist();
  }, [resolvedParams.slug]);

  // Check if current user follows this artist
  useEffect(() => {
    if (!user?.uid || !artist) return;
    isFollowing(user.uid, artist.id || resolvedParams.slug).then(docId => {
      setIsFollowingArtist(!!docId);
    });
  }, [user?.uid, artist, resolvedParams.slug]);

  // ── Attach existing works / releases / videos to this artist ──────────────
  const attachCollFor = (tab: "track" | "release" | "video") => tab === "release" ? "releases" : tab === "video" ? "videos" : "works";
  const loadAttachCandidates = useCallback(async (tab: "track" | "release" | "video") => {
    setAttachLoading(true);
    try {
      const snap = await getDocs(query(collection(db, attachCollFor(tab)), limit(400)));
      setAttachCandidates(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
    } catch { setAttachCandidates([]); }
    setAttachLoading(false);
  }, []);
  useEffect(() => { if (attachOpen) loadAttachCandidates(attachTab); }, [attachOpen, attachTab, loadAttachCandidates]);

  const isAttachedToThis = (item: any, tab: "track" | "release" | "video") =>
    item.artistId === artist?.id || item.artist?.docId === artist?.id;

  const toggleAttach = async (item: any, tab: "track" | "release" | "video", link: boolean) => {
    if (!artist) return;
    setAttachBusyId(item.id);
    try {
      const coll = attachCollFor(tab);
      const patch: Record<string, any> = link
        ? { artistId: artist.id, artistName: artist.name, artist: tab === "video" ? { docId: artist.id, name: artist.name } : artist.name }
        : { artistId: "", artistName: "", ...(tab === "video" ? { artist: deleteField() } : {}) };
      await updateDoc(doc(db, coll, item.id), patch);
      const patched = { ...item, artistId: link ? artist.id : "", artistName: link ? artist.name : "" };
      // Reflect in the open dialog list
      setAttachCandidates(prev => prev.map(c => c.id === item.id ? patched : c));
      // Reflect in the artist page tabs immediately
      if (tab === "track") {
        setWorks(prev => link ? [patched, ...prev.filter(w => w.id !== item.id)] : prev.filter(w => w.id !== item.id));
      } else if (tab === "release") {
        setReleases(prev => link ? [patched, ...prev.filter(r => r.id !== item.id)] : prev.filter(r => r.id !== item.id));
      } else {
        setVideos(prev => link ? [patched, ...prev.filter(v => v.id !== item.id)] : prev.filter(v => v.id !== item.id));
      }
      toast({ title: link ? "Attached" : "Detached", description: `${item.title || "Item"} ${link ? "linked to" : "unlinked from"} ${artist.name}.` });
    } catch (e: any) {
      toast({ title: "Couldn't update", description: (e?.message || String(e)).slice(0, 140), variant: "destructive" });
    }
    setAttachBusyId("");
  };

  const handleCreateRelease = async () => {
    if (!newReleaseTitle.trim() || !artist) return;
    setIsSavingRelease(true);
    try {
      await addDoc(fbCollection(db, "releases"), {
        title: newReleaseTitle.trim(),
        releaseType: newReleaseType.toLowerCase(),
        artist: artist.name,
        artistName: artist.name,
        artistId: artist.id,
        coverArtUrl: newReleaseCoverUrl.trim() || "",
        genres: artist.genres || [],
        genre: artist.genres?.[0] || "",
        tracks: [],
        status: "released",
        releaseDate: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdByUid: user?.uid || "",
      });
      toast({ title: "Release created", description: `"${newReleaseTitle}" added. Add tracks in Super Admin → Releases.` });
      setAddReleaseOpen(false);
      setNewReleaseTitle("");
      setNewReleaseType("Album");
      setNewReleaseCoverUrl("");
      // Reload the page to show the new release
      router.refresh();
    } catch (err) {
      console.error("Error creating release:", err);
      toast({ variant: "destructive", title: "Error", description: "Could not create release." });
    } finally {
      setIsSavingRelease(false);
    }
  };

  const handleFollow = guardAction(async () => {
    if (!artist || !user?.uid) return;
    setFollowLoading(true);
    try {
      const followerName = (userProfile as any)?.displayName || user.email || 'Someone';
      const result = await toggleFollow(user.uid, {
        targetId: artist.id || resolvedParams.slug,
        targetType: 'artist',
        targetName: artist.name,
        targetSlug: artist.slug || resolvedParams.slug,
        targetImageUrl: (artist as any).profileImageUrl || artist.imageUrl || '',
      }, { fromUserName: followerName, fromUserImageUrl: (userProfile as any)?.profileImageUrl || '' });
      setIsFollowingArtist(result.following);
      // Notify the artist's crew room when a new follow happens
      if (result.following) {
        notifyArtistNewFollower(
          artist.id || resolvedParams.slug,
          artist.name,
          followerName,
          (userProfile as any)?.profileImageUrl || ''
        ).catch(() => {}); // fire-and-forget

        // Show rich follow confirmation popup
        const snippets = releases.slice(0, 4).map((r: any) => ({
          id: r.id,
          title: r.title || r.name || 'Release',
          imageUrl: r.coverArtUrl || r.imageUrl || '',
          type: 'release' as const,
        }));
        setFollowPopupData({
          targetId: artist.id || resolvedParams.slug,
          targetName: artist.name,
          targetImageUrl: (artist as any).profileImageUrl || artist.imageUrl || '',
          bio: (artist as any).bio || '',
          followerCount: (artist as any).followerCount,
          followingCount: (artist as any).followingCount,
          typeLabel: 'Artist',
          profileLink: `/artists/${artist.slug || resolvedParams.slug}`,
          snippets,
        });
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    } finally {
      setFollowLoading(false);
    }
  });

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "";
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.getFullYear().toString();
  };

  // Separate releases by type (case-insensitive — Firestore stores lowercase)
  const rt = (r: any) => (r.releaseType || "").toLowerCase();
  const albums = releases.filter((r: any) => rt(r) === "album" || rt(r) === "lp");
  const eps = releases.filter((r: any) => rt(r) === "ep");
  const singles = releases.filter((r: any) => rt(r) === "single");
  const demoReleases = releases.filter((r: any) =>
    ["demo", "demos", "demo compilation", "beat", "beat pack"].includes(rt(r))
  );
  const otherReleases = releases.filter((r: any) =>
    !["album", "lp", "ep", "single", "demo", "demos", "demo compilation", "beat", "beat pack"].includes(rt(r))
  );

  // Build playable tracks from releases ordered: Albums → EPs → Singles → Demos → Other
  const orderedReleases = [...albums, ...eps, ...singles, ...demoReleases, ...otherReleases];

  // Build a fresh audioUrl map from the works collection (workId → work record).
  // Release docs embed a snapshot of each track's mp3Url at link-time, but
  // works in Firestore hold the authoritative, current audioUrl. Prefer the
  // work's audioUrl so we always get a valid, playable URL.
  const workById = new Map<string, any>();
  works.forEach((w: any) => { if (w.id) workById.set(w.id, w); });

  // Also index works by title for fallback matching when workId wasn't stored
  const workByTitle = new Map<string, any>();
  works.forEach((w: any) => {
    if (w.title) {
      const key = w.title.toLowerCase().trim();
      if (!workByTitle.has(key)) workByTitle.set(key, w);
    }
  });

  // Build release cover map from already-loaded releases so standalone works
  // without their own coverArtUrl can fall back to their parent release art.
  const workReleaseCoverMap = new Map<string, string>();
  releases.forEach((rel: any) => {
    const cover = rel.coverArtUrl || rel.imageUrl || '';
    if (!cover) return;
    if (Array.isArray(rel.tracks)) {
      rel.tracks.forEach((t: any) => {
        const wid = t.workId || t.id;
        if (wid && !workReleaseCoverMap.has(wid)) workReleaseCoverMap.set(wid, cover);
      });
    }
  });

  const worksPlayable = works
    .filter((w: any) => w.mp3Url || w.audioUrl || w.sourceUrl || w.previewUrl)
    .map((w: any) => ({
      id: w.id,
      title: w.title || "Untitled",
      // Credited (contributor/rights) tracks belong to another primary artist —
      // show their real credited name, not this profile's own name. `artist` may
      // be a plain string (Media Vault's Artist/Creator field) or an EntityRef
      // object — prefer whichever is actually set over the legacy `artistName`.
      artist: w._creditedVia
        ? ((typeof w.artist === 'string' ? w.artist : w.artist?.name) || w.artistName || "Unknown Artist")
        : ((typeof w.artist === 'string' ? w.artist : w.artist?.name) || artist?.name || "Unknown"),
      artistName: w._creditedVia ? ((typeof w.artist === 'string' ? w.artist : w.artist?.name) || w.artistName) : undefined,
      album: w.album?.title || "",
      duration: w.durationMs ? Math.round(w.durationMs / 1000) : (w.duration || 0),
      // 3-tier: work's own art → release art → artist profile image
      imageUrl: w.coverArtUrl || w.imageUrl || workReleaseCoverMap.get(w.id) || artist?.imageUrl || "",
      // Prefer mp3Url (converted M4A/MP3 — always browser-safe) over the raw
      // audioUrl which may still be an original AIF/WAV file.
      audioUrl: w.mp3Url || w.audioUrl || w.sourceUrl || w.previewUrl || "",
      mediaType: "audio" as const,
      genre: w.genre || "",
      artistSlug: artist?.slug || resolvedParams.slug,
      _creditedVia: w._creditedVia,
    }));

  // Build workId set (all works that have audio) for dedup
  const workIdSet = new Set(worksPlayable.map((w: any) => w.id));

  const allPlayableTracks = orderedReleases.flatMap((rel: any) =>
    (rel.tracks || [])
      .map((t: any, idx: number) => {
        // Prefer the work's converted mp3Url over the raw audioUrl — the raw
        // audioUrl may be an original AIF/WAV that Chrome cannot play.
        const linkedWork = (t.workId && workById.get(t.workId))
          || workByTitle.get((t.title || "").toLowerCase().trim());
        const freshAudioUrl = linkedWork?.mp3Url || linkedWork?.audioUrl || linkedWork?.sourceUrl || linkedWork?.previewUrl || "";
        const fallbackUrl = t.mp3Url || t.sourceUrl || t.audioUrl || "";
        const audioUrl = freshAudioUrl || fallbackUrl;
        if (!audioUrl) return null;
        // Use workId as track id when available so dedup matches worksPlayable;
        // else address by 1-based trackNumber (per-track docs are "-track-{n}"),
        // NOT the 0-based array index (that linked tracks one off).
        const id = t.workId || linkedWork?.id || `${rel.id}-track-${t.trackNumber || idx + 1}`;
        return {
          id,
          title: t.title || `Track ${t.trackNumber || idx + 1}`,
          artist: t.artistName || rel.artistName || artist?.name || "Unknown",
          album: rel.title || "",
          duration: t.durationMs ? Math.round(t.durationMs / 1000) : (linkedWork?.durationMs ? Math.round(linkedWork.durationMs / 1000) : 0),
          imageUrl: rel.coverArtUrl || rel.imageUrl || linkedWork?.coverArtUrl || artist?.imageUrl || "",
          audioUrl,
          mediaType: "audio" as const,
          genre: rel.genres?.[0] || linkedWork?.genre || "",
          artistSlug: artist?.slug || resolvedParams.slug,
        };
      })
      .filter(Boolean)
  ) as { id: string; title: string; artist: string; album: string; duration: number; imageUrl: string; audioUrl: string; mediaType: "audio"; genre: string; artistSlug: string; }[];

  // Merge: release tracks first (they carry release metadata like album title + cover).
  // Then append any works that weren't already included via a release.
  // Dedup by track id, then by audioUrl as secondary guard.
  const seenIds = new Set<string>();
  const seenUrls = new Set<string>();
  const mergedTracks = [
    ...allPlayableTracks,
    // Only add works that aren't already represented via a release track
    ...worksPlayable.filter((w: any) => !workIdSet.has(w.id) || !seenIds.has(w.id)),
  ].filter(t => {
    if (!t.audioUrl) return false;
    if (seenIds.has(t.id) || seenUrls.has(t.audioUrl)) return false;
    seenIds.add(t.id);
    seenUrls.add(t.audioUrl);
    return true;
  });

  // Works linked to this artist that can't stream yet (no converted audio) —
  // shown to admins in the Tracks tab so attached-but-unconverted uploads are
  // visible instead of silently missing.
  const streamableIds = new Set(mergedTracks.map((t: any) => t.id));
  const hiddenLinkedTracks = works.filter((w: any) =>
    !streamableIds.has(w.id) &&
    !(w.type === 'video' || w.videoUrl || w.youtubeId) &&
    !(w.mp3Url || w.audioUrl || w.sourceUrl || w.previewUrl));

  // "Play All" plays this artist's OWN catalog only — never tracks where a
  // linked member is merely credited on someone else's release (Gordon
  // 2026-09-21: Blacknuss Allstars' Play All was queuing 33 Absent Minded
  // tracks because Adam Baptiste is connected to both artist profiles).
  const ownMergedTracks = mergedTracks.filter((t: any) => !t._creditedVia);

  // Is the player currently playing one of this artist's own tracks?
  const isPlayingArtist = isPlaying && currentTrack && ownMergedTracks.some(t => t.id === currentTrack.id);

  const playAllTracks = () => {
    if (isPlayingArtist) {
      // Already playing this artist — toggle pause/resume
      togglePlay();
      return;
    }
    if (ownMergedTracks.length > 0) {
      playTracks(ownMergedTracks);
    } else {
      toast({ title: 'No playable tracks', description: 'This artist has no tracks with streaming audio. AIF files may need conversion first.', variant: 'destructive' });
    }
  };

  // Admin: unlink a release from this artist (clears artistId + artistName on the release doc)
  const handleUnlinkRelease = async (releaseId: string, releaseTitle: string) => {
    if (!confirm(`Unlink "${releaseTitle}" from ${artist?.name}? The release will still exist but won't appear on this artist page.`)) return;
    try {
      await updateDoc(doc(db, 'releases', releaseId), {
        artistId: deleteField(),
        artistName: deleteField(),
      });
      setReleases(prev => prev.filter(r => r.id !== releaseId));
      toast({ title: 'Release unlinked', description: `"${releaseTitle}" removed from this artist.` });
    } catch (err: any) {
      console.error('[unlink-release]', err);
      toast({ title: 'Failed to unlink', description: err.message, variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading artist...</p>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Users className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Artist not found</h2>
          <p className="text-muted-foreground mb-4">This artist profile doesn&apos;t exist.</p>
          <Link href="/artists"><Button variant="outline"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button></Link>
        </div>
      </div>
    );
  }

  const bio = artist.longBio || artist.bio || "";
  const bioTruncated = bio.length > 400;

  // Artist doc links (admin/owner edited map) + the linked owner account's own.
  const artistLinks = normalizeSocialLinks(artist.socialLinks, ownerLinks);
  const socialIconLinks = artistLinks.filter(l => l.kind !== "streaming");

  return (
    <div className="min-h-screen bg-background text-foreground w-full">
      <MusicGroupLD
        name={artist.name}
        genre={Array.isArray((artist as any).genres) ? (artist as any).genres[0] : (artist as any).genre}
        imageUrl={artist.imageUrl}
        url={`${process.env.NEXT_PUBLIC_APP_URL || 'https://breakinbread.com'}/artists/${resolvedParams.slug}`}
        description={(artist as any).bio}
        sameAs={Array.from(new Set([(artist as any).instagramUrl, (artist as any).spotifyUrl, (artist as any).youtubeUrl, (artist as any).soundcloudUrl, ...artistLinks.map(l => l.url)].filter(Boolean))) as string[]}
      />
      {/* Breadcrumb bar */}
      <div className="border-b border-border/50 bg-background/95 backdrop-blur sticky top-[50px] z-30">
        <div className="w-full px-page py-1.5 flex items-center gap-3">
          <Breadcrumbs className="min-w-0 flex-1" items={[
            { label: t('title'), href: '/artists', icon: Users },
            { label: artist.name, icon: UserRound },
          ]} />
          {(isAdmin || isSuperAdmin) && artist.id && (
            <LinkedSessionBadge entityId={artist.id} />
          )}
          <div className="ml-auto">
            <AdminToolbar
              entityType="Artist"
              entityData={artist}
              isHidden={artist.adminHidden}
              ownerId={(artist as any).userId || (artist as any).createdByUid || (artist as any).createdBy}
              editFields={[]}
              // The pencil button opens the SAME full editor as the "Edit
              // Profile" button below (Gallery, Videos, thumbnail autoplay,
              // everything) instead of AdminToolbar's own generic
              // name/slug/bio/genres/image/status-only dialog — Gordon
              // 2026-09-20: "edit form is incomplete ... make it complete."
              // One editor for artists, not two that can drift apart.
              onEditClick={() => setEditProfileOpen(true)}
              onToggleVisibility={async (hidden) => toggleAdminHidden(artist.id, hidden)}
              onDelete={async () => deleteArtist(artist.id)}
              onDeleteRedirect={() => router.push('/home')}
            />
          </div>
        </div>
      </div>

      {/* ── Desktop split layout ── */}
      <div className="flex flex-col md:flex-row md:h-[calc(100vh_-_var(--header-height)_-_45px)] md:overflow-hidden">

        {/* ── LEFT PANEL: artist image + metadata + social + actions ── */}
        <div className={cn("md:w-[45%] md:min-h-0 md:overflow-y-auto md:border-r md:border-border/40 p-5 md:p-6 flex flex-col gap-3 md:gap-4", isPlayerActive && "md:pb-24")}>

          {/* Artist image — strict 1:1 square at all breakpoints.
              flex-shrink-0 stops the flex parent from stretching it
              vertically on tall desktop panels. Per Gordon: "1:1 ratio
              only for artists, releases, tracks, user avatar, playlist,
              favorites, marketplace items, events, radio thumbnails — all
              square." */}
          <div className="relative w-full aspect-square rounded-2xl overflow-hidden shadow-2xl bg-muted group flex-shrink-0">
            {/* Fall back to the gallery's thumbnail (images[0]) when no
                dedicated Profile Image is set — same "enrich from the
                related record" pattern as track/release covers. Without
                this, adding a Gallery image did nothing visible on the
                public page (Gordon 2026-09-30: "see it in edit window but
                doesn't update profile???"). */}
            {(artist.imageUrl || artist.images?.[0]) ? (
              <Image src={artist.imageUrl || artist.images![0]} alt={artist.name} fill className="object-cover" priority />
            ) : (
              <>
                <GenerativeArtistAvatar
                  name={artist.name}
                  size={400}
                  className="absolute inset-0 w-full h-full"
                  animate
                  analyserNode={analyserL}
                  patternOverride={avatarPattern}
                  bgColor={avatarBg}
                  fgColor={avatarFg}
                  fontFamily={avatarFont}
                />
                {/* Customizer button — visible on hover for admins/creators */}
                {(isAdmin || isSuperAdmin) && (
                  <div className="absolute bottom-2 right-2 z-10 bb-hover-reveal transition-opacity">
                    <GenerativeAvatarCustomizer
                      artistId={artist.id || ''}
                      artistName={artist.name}
                      currentConfig={{
                        patternIndex: avatarPattern ?? null,
                        bgColor: avatarBg ?? null,
                        fgColor: avatarFg ?? null,
                        fontFamily: avatarFont ?? null,
                      }}
                      onSave={(snap) => {
                        setAvatarPattern(snap.patternIndex ?? undefined);
                        setAvatarBg(snap.bgColor ?? undefined);
                        setAvatarFg(snap.fgColor ?? undefined);
                        setAvatarFont(snap.fontFamily ?? undefined);
                      }}
                      compact
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Name + metadata */}
          <div className="space-y-1.5 flex-shrink-0">
            <h1 className="capitalize text-2xl sm:text-3xl font-bold tracking-tight leading-tight">{artist.name}</h1>
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {artist.genres && artist.genres.length > 0 && (
                <span className="flex items-center gap-1.5"><Music2 className="h-3.5 w-3.5" />{artist.genres.join(", ")}</span>
              )}
              {artist.origin && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" />
                  {artist.origin.city ? `${artist.origin.city}, ` : ""}{artist.origin.country}
                </span>
              )}
              {artist.yearsActive && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {artist.yearsActive.from}{artist.yearsActive.to ? ` – ${artist.yearsActive.to}` : " – present"}
                </span>
              )}
              {artist.labelName && (
                <Link href={`/labels/${artist.labelId || "breakin-bread"}`}>
                  <Badge variant="outline" className="text-xs hover:bg-primary/10 transition-colors cursor-pointer">{artist.labelName}</Badge>
                </Link>
              )}
            </div>
          </div>

          {/* Social links — streaming services are in "Listen on" below */}
          {(socialIconLinks.length > 0 || artist.discogs) && (
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              {socialIconLinks.map((account) => {
                const config = socialPlatformConfig[account.id] || socialPlatformConfig.website;
                const SocialIcon = config.icon;
                const label = account.id === "website" ? (account.customLabel || config.label) : account.label;
                return (
                  <Button key={`${account.id}-${account.url}`} asChild variant="outline" size="icon" className={`h-11 w-11 ${config.color} transition-colors`}>
                    <a href={account.url} target="_blank" rel="noopener noreferrer" title={label} aria-label={label}>
                      <SocialIcon />
                    </a>
                  </Button>
                );
              })}
              {artist.discogs && (
                <Button asChild variant="outline" size="icon" className="h-11 w-11 hover:text-foreground transition-colors">
                  <a href={`https://www.discogs.com/artist/${artist.discogs}`} target="_blank" rel="noopener noreferrer" title="Discogs" aria-label="Discogs">
                    <DiscogsIcon />
                  </a>
                </Button>
              )}
            </div>
          )}

          {/* "Listen on" — the artist's streaming links plus the linked owner
              account's own (Profile -> Social), each one shareable. */}
          <LinkedAccounts links={artistLinks} name={artist.name} groups={["listen"]} />

          {/* Action buttons — Play All first */}
          <div className="flex flex-wrap gap-2 flex-shrink-0">
            <Button
              variant={isPlayingArtist ? "default" : "outline"}
              size="sm"
              className={cn("gap-1.5", isPlayingArtist && "bg-primary text-primary-foreground")}
              onClick={playAllTracks}
            >
              {isPlayingArtist ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {isPlayingArtist ? 'Playing' : 'Play All'}
            </Button>
            <Button
              size="sm" className="gap-1.5"
              variant={isFollowingArtist ? "default" : "outline"}
              onClick={handleFollow} disabled={followLoading}
            >
              <Users className="h-4 w-4" />
              {followLoading ? '...' : isFollowingArtist ? tCommon('following') : tCommon('follow')}
            </Button>
            <SaveButton
              itemId={artist.id || resolvedParams.slug}
              itemType="artist"
              title={artist.name}
              subtitle={artist.genres?.join(', ') || 'Artist'}
              imageUrl={artist.imageUrl || ''}
              link={`/artists/${artist.slug || resolvedParams.slug}`}
            />
            {/* Send Message — hidden when the artist isn't connected to a
                user/creator account. Per Gordon: "artist not connected to
                a user creator account should not show send message."
                Same userId chain we check inside handleMessage; rendering
                the button only when it can actually work beats clicking
                it and getting a "not linked to a platform account" toast. */}
            {((artist as any)?.userId || (artist as any)?.createdByUid || (artist as any)?.createdBy) && (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handleMessage}>
                <MessageCircle className="h-4 w-4" />{tProfile('message')}
              </Button>
            )}
            <ShareButton
              title={artist.name}
              variant="outline"
              iconOnly
              size="icon"
              className="h-9 w-9"
              onShare={() => trackMediaEvent({
                type: 'share',
                entityType: 'artist',
                entityId: artist.id || resolvedParams.slug,
                entityTitle: artist.name,
              })}
            />
            <QRShareButton title={artist.name} subtitle="Artist" imageUrl={artist.imageUrl} />

            {/* Mentor this artist — PWYW, routed to their Connect account */}
            <SponsorButton
              kind="mentor"
              entityType="creator"
              recipientId={(artist as any)?.userId || (artist as any)?.createdByUid || (artist as any)?.createdBy || ''}
              recipientName={artist.name}
              entityTitle={artist.name}
              coverArtUrl={artist.imageUrl || ''}
              label="Support"
            />

            {/* Admin or connected owner/manager: Edit Profile WYSIWYG */}
            {(isAdmin || isSuperAdmin || isOwner) && (
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditProfileOpen(true)}>
                <Pencil className="h-4 w-4" />Edit Profile
              </Button>
            )}

            {/* Admin or connected owner/manager: Add Content */}
            {(isAdmin || isSuperAdmin || isOwner) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="secondary" className="gap-1.5">
                    <PlusCircle className="h-4 w-4" />{tCommon('add')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{tAdmin('addContent')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setAddReleaseOpen(true)}>
                    <Disc className="h-4 w-4 mr-2" />{tAdmin('newRelease')}
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href={`/media?artist=${encodeURIComponent(artist.name)}&artistId=${artist.id}&tab=bulk`}>
                      <Upload className="h-4 w-4 mr-2" />{t('uploadTracks')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { setAttachTab("track"); setAttachSearch(""); setAttachOpen(true); }}>
                    <Music2 className="h-4 w-4 mr-2" />Attach existing track
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setAttachTab("release"); setAttachSearch(""); setAttachOpen(true); }}>
                    <Disc className="h-4 w-4 mr-2" />Attach existing release
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setAttachTab("video"); setAttachSearch(""); setAttachOpen(true); }}>
                    <Video className="h-4 w-4 mr-2" />Attach existing video
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Create Release dialog */}
          <Dialog open={addReleaseOpen} onOpenChange={setAddReleaseOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>New Release for {artist.name}</DialogTitle>
                <DialogDescription>Creates an album/EP/single entry. Add tracks afterward in Super Admin → Releases.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <UILabel className="text-xs">Release Title *</UILabel>
                  <UIInput
                    value={newReleaseTitle}
                    onChange={(e) => setNewReleaseTitle(e.target.value)}
                    placeholder="e.g. Extreme Paranoia in Stocktown"
                    className="mt-1"
                  />
                </div>
                <div>
                  <UILabel className="text-xs">Release Type</UILabel>
                  <UISelect value={newReleaseType} onValueChange={setNewReleaseType}>
                    <UISelectTrigger className="mt-1">
                      <UISelectValue />
                    </UISelectTrigger>
                    <UISelectContent>
                      {["Album", "EP", "Single", "Mixtape", "Compilation", "Live Album", "Demo Compilation"].map((t) => (
                        <UISelectItem key={t} value={t}>{t}</UISelectItem>
                      ))}
                    </UISelectContent>
                  </UISelect>
                </div>
                <div>
                  <UILabel className="text-xs">Cover Art URL (optional)</UILabel>
                  <UIInput
                    value={newReleaseCoverUrl}
                    onChange={(e) => setNewReleaseCoverUrl(e.target.value)}
                    placeholder="https://..."
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleCreateRelease}
                    disabled={isSavingRelease || !newReleaseTitle.trim()}
                    className="flex-1"
                  >
                    {isSavingRelease ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {tCommon('create')}
                  </Button>
                  <Button variant="outline" onClick={() => setAddReleaseOpen(false)}>{tCommon('cancel')}</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Attach existing works / releases / videos to this artist */}
          <Dialog open={attachOpen} onOpenChange={setAttachOpen}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>Attach to {artist.name}</DialogTitle>
                <DialogDescription>Link existing {attachTab === "track" ? "tracks" : attachTab === "release" ? "releases" : "videos"} to this artist. Already-linked items show a green check.</DialogDescription>
              </DialogHeader>
              {/* Tab switcher */}
              <div className="flex gap-1 rounded-lg bg-muted/40 p-1">
                {([["track", "Tracks"], ["release", "Releases"], ["video", "Videos"]] as const).map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setAttachTab(k)}
                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${attachTab === k ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <UIInput value={attachSearch} onChange={e => setAttachSearch(e.target.value)} placeholder="Search by title or artist…" className="h-9" />
              <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1 min-h-[240px]">
                {attachLoading ? (
                  <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : (() => {
                  const q = attachSearch.trim().toLowerCase();
                  const rows = attachCandidates
                    .filter(c => {
                      if (!q) return true;
                      const nm = (typeof c.artist === "string" ? c.artist : c.artist?.name) || c.artistName || "";
                      return (c.title || "").toLowerCase().includes(q) || nm.toLowerCase().includes(q);
                    })
                    // linked-to-this first, then the rest
                    .sort((a, b) => Number(isAttachedToThis(b, attachTab)) - Number(isAttachedToThis(a, attachTab)))
                    .slice(0, 80);
                  if (rows.length === 0) return <p className="text-center text-sm text-muted-foreground py-10">Nothing found.</p>;
                  return rows.map(c => {
                    const linked = isAttachedToThis(c, attachTab);
                    const nm = (typeof c.artist === "string" ? c.artist : c.artist?.name) || c.artistName || "—";
                    const thumb = c.coverArtUrl || c.imageUrl || c.thumbnailUrl || (c.youtubeId ? getThumbnailUrl(c.youtubeId, "mqdefault") : "");
                    const noAudio = attachTab === "track" && !(c.mp3Url || c.audioUrl || c.sourceUrl || c.previewUrl) && !c.videoUrl && !c.youtubeId;
                    return (
                      <div key={c.id} className={`flex items-center gap-2.5 rounded-lg border p-2 ${linked ? "border-primary/50 bg-primary/5" : "border-border/50"}`}>
                        <Avatar className="h-9 w-9 rounded-md flex-shrink-0">
                          <AvatarImage src={thumb} />
                          <AvatarFallback className="rounded-md">{attachTab === "video" ? <Video className="h-4 w-4" /> : attachTab === "release" ? <Disc className="h-4 w-4" /> : <Music2 className="h-4 w-4" />}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{c.title || "Untitled"}</p>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {nm}{c.type ? ` · ${c.type}` : ""}{noAudio ? " · ⚠ no streaming audio" : ""}
                          </p>
                        </div>
                        <Button size="sm" variant={linked ? "outline" : "secondary"} className="h-7 text-xs flex-shrink-0"
                          disabled={attachBusyId === c.id}
                          onClick={() => toggleAttach(c, attachTab, !linked)}>
                          {attachBusyId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : linked ? <><Check className="h-3.5 w-3.5 mr-1" />Linked</> : "Attach"}
                        </Button>
                      </div>
                    );
                  });
                })()}
              </div>
              <p className="text-[11px] text-muted-foreground">Tip: tracks without streaming audio still attach, but need AIF/WAV conversion in the Media Vault before they can play.</p>
            </DialogContent>
          </Dialog>

          {/* Bio (left panel on desktop, full) */}
          {bio && (
            <div className="hidden md:block space-y-2 flex-shrink-0">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">About</h2>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                {bioTruncated && !showFullBio ? bio.substring(0, 500) + "..." : bio}
              </p>
              {bioTruncated && (
                <Button variant="ghost" size="sm" className="text-primary px-0" onClick={() => setShowFullBio(!showFullBio)}>
                  {showFullBio ? <><ChevronUp className="h-4 w-4 mr-1" />Show less</> : <><ChevronDown className="h-4 w-4 mr-1" />Read more</>}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* ── RIGHT PANEL: bio (mobile) + content tabs ── */}
        <div className={cn("md:flex-1 md:min-h-0 md:overflow-y-auto p-4 md:p-8 flex flex-col gap-6", isPlayerActive && "md:pb-24")}>

          {/* Bio on mobile only */}
          {bio && (
            <div className="md:hidden space-y-2">
              <h2 className="text-base font-semibold">About</h2>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                {bioTruncated && !showFullBio ? bio.substring(0, 400) + "..." : bio}
              </p>
              {bioTruncated && (
                <Button variant="ghost" size="sm" className="text-primary px-0" onClick={() => setShowFullBio(!showFullBio)}>
                  {showFullBio ? <><ChevronUp className="h-4 w-4 mr-1" />Show less</> : <><ChevronDown className="h-4 w-4 mr-1" />Read more</>}
                </Button>
              )}
            </div>
          )}

          {/* Tabs */}
          <ArtistContentTabs
            artist={artist}
            albums={albums} eps={eps} singles={singles} demoReleases={demoReleases} otherReleases={otherReleases}
            featuredReleases={featuredReleases}
            mergedTracks={mergedTracks} hiddenLinkedTracks={hiddenLinkedTracks} videos={videos} news={news} shopItems={shopItems}
            formatDate={formatDate}
            playTracks={playTracks}
            isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} isOwner={isOwner}
            resolvedSlug={resolvedParams.slug}
            onUnlinkRelease={handleUnlinkRelease}
            currentUserId={user?.uid}
            onOpenEditProfile={() => setEditProfileOpen(true)}
            linkedAccounts={artistLinks}
          />

          {/* You might also like — genre-matched artists */}
          {similarArtists.length > 0 && (
            <div className="mt-8">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-base font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />You might also like
                </h3>
                <PerRowPicker value={similarPerRow} onChange={setSimilarPerRow} />
              </div>
              {/* Full width, 1–4 tiles per row on phones (2 by default), double
                  from tablet up — Gordon 2026-10-31. */}
              <div className={cn("grid gap-3", PER_ROW_GRID[similarPerRow])}>
                {similarArtists.map((a: any) => {
                  const slug = a.slug || (a.name || '').toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
                  const img = a.imageUrl || a.featuredImageUrl || '';
                  return (
                    <Link key={a.id} href={`/artists/${slug}`} className="group min-w-0">
                      <Card className="bg-card/50 border-border/50 hover:border-primary/30 transition-all hover:shadow-lg overflow-hidden">
                        <div className="relative aspect-square w-full bg-primary/10">
                          {img ? (
                            <Image src={img} alt={a.name || ''} fill unoptimized className="object-cover group-hover:scale-105 transition-transform duration-300" />
                          ) : (
                            <GenerativeArtistAvatar name={a.name || '?'} size={300} className="w-full h-full" animate />
                          )}
                        </div>
                        <CardContent className="p-2.5">
                          <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">{a.name}</p>
                          {a.genres?.[0] && <p className="text-xs text-muted-foreground mt-0.5 capitalize truncate">{a.genres[0]}</p>}
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bottom spacer */}
          <div className="h-16" />
        </div>
      </div>

      {/* Rich follow confirmation popup */}
      {followPopupData && (
        <FollowConfirmPopup
          data={followPopupData}
          onClose={() => setFollowPopupData(null)}
        />
      )}

      {/* Edit Profile dialog (admins/creators) */}
      {editProfileOpen && (
        <ArtistProfileEditDialog
          open={editProfileOpen}
          onOpenChange={setEditProfileOpen}
          artist={artist}
          onSaved={(updated) => {
            setArtist(prev => prev ? { ...prev, ...updated } as any : prev);
          }}
        />
      )}
    </div>
  );
}

// Release section component
function ReleaseSection({ title, releases, formatDate, canManage, onUnlink, relationLabel }: {
  title: string; releases: any[]; formatDate: (ts: any) => string;
  canManage?: boolean; onUnlink?: (releaseId: string, releaseTitle: string) => void;
  /** Per-card badge (e.g. "Featuring" or "with Nirvana") for mixed-source sections like "Featured On". */
  relationLabel?: (release: any) => string | undefined;
}) {
  return (
    <div className="mt-6">
      <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
        <Disc className="h-4 w-4 text-primary" />{title}
      </h3>
      {/* Desktop: FEWER columns = release covers about twice the size
          (Gordon: 'artist releases thumbnails can be larger in desktop…
          like twice as large'). Mobile unchanged. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-3 lg:gap-5">
        {releases.map((release: any) => {
          const badge = relationLabel?.(release);
          return (
          <TiltCard key={release.id} className="relative group">
            <Link href={`/music/${release.id}`}>
              <Card className="overflow-hidden border-border/50 hover:border-primary/30 transition-all hover:shadow-lg">
                <div className="relative aspect-square rounded-xl overflow-hidden bg-muted">
                  {(release.coverArtUrl || release.imageUrl) ? (
                    <Image src={release.coverArtUrl || release.imageUrl} alt={release.title} fill
                      className="object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center"><Disc className="h-8 w-8 text-muted-foreground/20" /></div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                    <Play className="h-8 w-8 text-white" />
                  </div>
                  {badge && (
                    <span className="absolute top-1.5 left-1.5 z-10 rounded-full bg-black/70 text-white text-[9px] font-medium px-2 py-0.5 backdrop-blur-sm">
                      {badge}
                    </span>
                  )}
                  <CoverExplicitSticker explicit={release.explicit} />
                </div>
                <CardContent className="p-2.5">
                  <p className="text-xs font-medium truncate">{release.title}</p>
                  <p className="text-[10px] text-muted-foreground">{release.releaseType || title} · {formatDate(release.releaseDate)}</p>
                </CardContent>
              </Card>
            </Link>
            {/* Admin: unlink this release from the artist */}
            {canManage && onUnlink && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onUnlink(release.id, release.title); }}
                className="absolute top-1.5 right-1.5 z-10 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center bb-hover-reveal transition-opacity hover:bg-destructive"
                title="Unlink from this artist"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </TiltCard>
          );
        })}
      </div>
    </div>
  );
}

/** One row in the Tracks tab list — shared by the artist's own tracks and the
 *  separate Credits section (see ArtistContentTabs). `index` is the track's
 *  position within whichever list `onPlay` queues, so playback and the "up
 *  next" queue never mix own tracks with credited ones. */
function TrackRow({ track, index, artist, onPlay }: { track: any; index: number; artist: any; onPlay: () => void }) {
  return (
    <button onClick={onPlay}
      className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-secondary/50 transition-colors w-full text-left">
      <span className="text-xs text-muted-foreground w-6 text-right flex-shrink-0">{index + 1}</span>
      <Avatar className="h-9 w-9 rounded-md flex-shrink-0">
        <AvatarImage src={track.imageUrl} />
        <AvatarFallback className="rounded-md"><Music2 className="h-4 w-4" /></AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate flex items-center gap-1.5">
          {track.title}
          {track._creditedVia && (
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 font-normal flex-shrink-0">
              {track._creditedVia === 'rights' ? 'Rights' : 'Credit'}
            </Badge>
          )}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {track._creditedVia
            ? [track.artist, track.album || track.genre].filter(Boolean).join(' · ')
            : (track.album || track.genre || "")}
        </p>
      </div>
      {track.duration > 0 && (
        <span className="text-xs text-muted-foreground flex-shrink-0">
          {Math.floor(track.duration / 60)}:{String(track.duration % 60).padStart(2, "0")}
        </span>
      )}
      <Play className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      {track.allowDownloads !== false && (
        <span onClick={e => e.stopPropagation()}>
          <DownloadButton
            item={{
              id: track.id || track.workId,
              title: track.title,
              artist: artist.name,
              coverArtUrl: track.imageUrl || artist.imageUrl,
              downloadType: track.downloadType || "pwyw",
              downloadMinPrice: track.downloadMinPrice ?? 0,
              downloadCurrency: track.downloadCurrency,
              hiResUrl: track.hiResUrl,
            }}
            compact
          />
        </span>
      )}
    </button>
  );
}

// ─── Artist Content Tabs component ───────────────────────────────────────────
function ArtistContentTabs({
  artist, albums, eps, singles, demoReleases, otherReleases, featuredReleases = [],
  mergedTracks, hiddenLinkedTracks = [], videos, news, shopItems, formatDate,
  playTracks, isAdmin, isSuperAdmin, isOwner = false, resolvedSlug, onUnlinkRelease, currentUserId,
  onOpenEditProfile, linkedAccounts = [],
}: {
  artist: any; albums: any[]; eps: any[]; singles: any[]; demoReleases: any[]; otherReleases: any[];
  /** Releases where this artist appears as a featured artist, or via a band/group they're a member of — never their own primary releases. */
  featuredReleases?: any[];
  mergedTracks: any[]; hiddenLinkedTracks?: any[]; videos: any[]; news: any[]; shopItems: any[];
  formatDate: (ts: any) => string;
  playTracks: (tracks: any[], idx?: number) => void;
  isAdmin: boolean; isSuperAdmin: boolean; isOwner?: boolean; resolvedSlug: string;
  onUnlinkRelease?: (releaseId: string, releaseTitle: string) => void;
  currentUserId?: string;
  /** Opens the full ArtistProfileEditDialog (Gallery, Videos, etc.) — used
   *  by the persistent "Add another video" tile below. */
  onOpenEditProfile?: () => void;
  /** Artist links + the linked owner's own (normalised). */
  linkedAccounts?: LinkedAccount[];
}) {
  const t = useTranslations('artists');
  const tMusic = useTranslations('music');
  const tProfile = useTranslations('profile');
  const hasReleases = albums.length + eps.length + singles.length + demoReleases.length + otherReleases.length + featuredReleases.length > 0;
  const canManage = isAdmin || isSuperAdmin || isOwner;
  // Tracks this artist actually released, vs tracks where a linked user account
  // is merely credited as a contributor/rights-holder on someone ELSE's track
  // (Gordon 2026-09-21: a musician connected to several of his own acts — e.g.
  // Adam Baptiste on Blacknuss Allstars, Absent Minded, Stonefunkers — was
  // having ALL of his Absent Minded credits bleed onto the Blacknuss Allstars
  // page, with nothing to distinguish them from Blacknuss's own catalog).
  // Own tracks decide the default tab and the "no releases yet" empty state;
  // credited tracks render in their own clearly-labeled section below.
  const ownTracks = mergedTracks.filter((mt: any) => !mt._creditedVia);
  const creditedTracks = mergedTracks.filter((mt: any) => mt._creditedVia);
  const autoTab = hasReleases ? "releases" : ownTracks.length > 0 ? "tracks" : canManage ? "releases" : "web";
  // ?tab=releases (breadcrumbs link here) picks the tab; unknown values fall back.
  const rawTab = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("tab") : null;
  const askedTab = rawTab === "merch" ? "shop" : rawTab;
  const defaultTab = askedTab && ["releases", "tracks", "videos", "gallery", "members", "news", "shop", "social", "web"].includes(askedTab) ? askedTab : autoTab;

  return (
    <Tabs defaultValue={defaultTab} className="w-full">
      <TabsList className="flex flex-wrap h-auto gap-1 justify-start bg-transparent p-0 mb-2">
        {(hasReleases || canManage) && <TabsTrigger value="releases" className="rounded-full border border-border/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary">{t('releases')}</TabsTrigger>}
        {(mergedTracks.length > 0 || canManage) && <TabsTrigger value="tracks" className="rounded-full border border-border/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary">{t('tracks')}</TabsTrigger>}
        {(artist.videoUrl || (Array.isArray(artist.videos) && artist.videos.filter(Boolean).length > 0) || videos.length > 0 || canManage) && <TabsTrigger value="videos" className="rounded-full border border-border/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary">{t('videos')}</TabsTrigger>}
        {artist.images && artist.images.filter(Boolean).length > 0 && <TabsTrigger value="gallery" className="rounded-full border border-border/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary">{t('gallery')}</TabsTrigger>}
        {artist.members && artist.members.length > 0 && <TabsTrigger value="members" className="rounded-full border border-border/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary">Members</TabsTrigger>}
        {news.length > 0 && <TabsTrigger value="news" className="rounded-full border border-border/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary">News</TabsTrigger>}
        {(shopItems.length > 0 || canManage) && <TabsTrigger value="shop" className="rounded-full border border-border/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary">{t('merch')}</TabsTrigger>}
        {linkedAccounts.length > 0 && (
          <TabsTrigger value="social" className="rounded-full border border-border/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary">{tProfile('social')}</TabsTrigger>
        )}
        <TabsTrigger value="web" className="rounded-full border border-border/50 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary">Web Info</TabsTrigger>
      </TabsList>

      {/* Releases */}
      {(hasReleases || canManage) && (
        <TabsContent value="releases" className="mt-2 space-y-1">
          {albums.length > 0 && <ReleaseSection title="Albums" releases={albums} formatDate={formatDate} canManage={canManage} onUnlink={onUnlinkRelease} />}
          {eps.length > 0 && <ReleaseSection title="EPs" releases={eps} formatDate={formatDate} canManage={canManage} onUnlink={onUnlinkRelease} />}
          {singles.length > 0 && <ReleaseSection title="Singles" releases={singles} formatDate={formatDate} canManage={canManage} onUnlink={onUnlinkRelease} />}
          {demoReleases.length > 0 && <ReleaseSection title="Demos" releases={demoReleases} formatDate={formatDate} canManage={canManage} onUnlink={onUnlinkRelease} />}
          {otherReleases.length > 0 && <ReleaseSection title="Other Releases" releases={otherReleases} formatDate={formatDate} canManage={canManage} onUnlink={onUnlinkRelease} />}
          {featuredReleases.length > 0 && (
            <ReleaseSection
              title="Featured On"
              releases={featuredReleases}
              formatDate={formatDate}
              relationLabel={(r: any) => r._relation === 'band' ? `with ${r._bandName}` : 'Featuring'}
            />
          )}
          {!hasReleases && canManage && (
            <div className="flex flex-col items-center justify-center py-12 text-center gap-3 border-2 border-dashed border-border/40 rounded-xl">
              <Disc className="h-10 w-10 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">{t('noReleases')}</p>
              <p className="text-xs text-muted-foreground/60">Use the <strong>+ Add</strong> button above to create an Album, EP, or Single.<br />Or use <strong>Album/EP Upload</strong> in the Media Vault to bulk-upload tracks.</p>
            </div>
          )}
        </TabsContent>
      )}

      {/* Tracks */}
      {(mergedTracks.length > 0 || canManage) && (
        <TabsContent value="tracks" className="mt-2">
          {ownTracks.length > 0 && (
            <div className="space-y-0.5">
              {ownTracks.map((track: any, i: number) => (
                <TrackRow key={track.id} track={track} index={i} artist={artist} onPlay={() => playTracks(ownTracks, i)} />
              ))}
            </div>
          )}
          {/* Credited (contributor/rights) tracks — kept in a clearly separate
              section, never merged into the list above. A linked member can be
              credited on tracks released under a DIFFERENT act; without this
              split, those tracks read as this artist's own catalog (Gordon
              2026-09-21: Absent Minded tracks appearing to be Blacknuss
              Allstars releases because Adam Baptiste is connected to both). */}
          {creditedTracks.length > 0 && (
            <div className={ownTracks.length > 0 ? "mt-6" : ""}>
              <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
                <Award className="h-4 w-4 text-primary" />Credits
              </h3>
              <p className="text-xs text-muted-foreground/70 mb-3">
                Tracks a member of {artist.name} is credited on — released by another artist, not part of this catalog.
              </p>
              <div className="space-y-0.5">
                {creditedTracks.map((track: any, i: number) => (
                  <TrackRow key={track.id} track={track} index={i} artist={artist} onPlay={() => playTracks(creditedTracks, i)} />
                ))}
              </div>
            </div>
          )}
          {/* Admin-only: linked works that can't stream (no converted audio).
              They're attached but excluded from the playable list above — surfaced
              here so admins can see them and convert the source in the Media Vault. */}
          {canManage && (() => {
            const hidden = hiddenLinkedTracks;
            if (hidden.length === 0) return null;
            return (
              <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                <p className="text-xs font-medium text-amber-500 flex items-center gap-1.5"><AlertTriangleIcon className="h-3.5 w-3.5" />{hidden.length} linked track{hidden.length === 1 ? '' : 's'} not streamable yet</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Attached to this artist but with no converted audio — convert the AIF/WAV in the Media Vault to make {hidden.length === 1 ? 'it' : 'them'} playable.</p>
                <div className="mt-2 space-y-1">
                  {hidden.map((w: any) => (
                    <div key={w.id} className="flex items-center gap-2 text-xs">
                      <Music2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="truncate flex-1">{w.title || 'Untitled'}</span>
                      <Link href={`/media?edit=${w.id}`} className="text-primary hover:underline flex-shrink-0">Open in Vault</Link>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {ownTracks.length === 0 && creditedTracks.length === 0 && canManage && (
            <div className="flex flex-col items-center justify-center py-12 text-center gap-3 border-2 border-dashed border-border/40 rounded-xl">
              <Music2 className="h-10 w-10 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">{t('noTracks')}</p>
              <p className="text-xs text-muted-foreground/60">Upload tracks in the Media Vault (Album/EP Upload), attach an existing track via the <strong>+ Add</strong> menu, or link works in Super Admin → Releases.</p>
            </div>
          )}
        </TabsContent>
      )}

      {/* Videos */}
      {(artist.videoUrl || (Array.isArray(artist.videos) && artist.videos.filter(Boolean).length > 0) || videos.length > 0 || canManage) && (
        <TabsContent value="videos" className="mt-2">
          {/* Single full-width column — the video fills the right info panel
              (Gordon: "the video should be full width on the artist page info
              right side") rather than sitting in a half-width 2-col grid. */}
          <div className="grid grid-cols-1 gap-6">
            {artist.videoUrl && (() => {
              const vidId = extractVideoId(artist.videoUrl) || artist.videoUrl;
              return (
                <div key="primary-video">
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-black shadow-lg">
                    <iframe src={getEmbedUrl(vidId, { controls: true, modestBranding: true })}
                      title={`${artist.name} Video`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen className="absolute inset-0 w-full h-full" />
                  </div>
                  <p className="text-sm font-medium mt-2 truncate">{artist.name}</p>
                  <p className="text-xs text-muted-foreground">Featured Video</p>
                </div>
              );
            })()}
            {/* Additional videos added via the edit dialog (artist.videos[] is
                a plain string[] of YouTube/direct URLs — separate from the
                BBVideo collection rendered below). Previously these were
                silently dropped on the public page. */}
            {Array.isArray(artist.videos) && artist.videos.filter(Boolean).map((vurl: string, i: number) => {
              const vidId = extractVideoId(vurl) || vurl;
              return (
                <div key={`extra-${i}`}>
                  <div className="relative aspect-video rounded-xl overflow-hidden bg-black shadow-lg">
                    <iframe src={getEmbedUrl(vidId, { controls: true, modestBranding: true })}
                      title={`${artist.name} Video ${i + 2}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen className="absolute inset-0 w-full h-full" />
                  </div>
                  <p className="text-sm font-medium mt-2 truncate">{artist.name}</p>
                  <p className="text-xs text-muted-foreground">Video {i + 2}</p>
                </div>
              );
            })}
            {videos.map((vid: any) => {
              const rawYtId = vid.youtubeId || vid.url || "";
              const youtubeId = extractVideoId(rawYtId) || rawYtId;
              return (
                <div key={vid.id}>
                  {youtubeId ? (
                    <div className="relative aspect-video rounded-xl overflow-hidden bg-black shadow-lg">
                      <iframe src={getEmbedUrl(youtubeId, { controls: true, modestBranding: true })}
                        title={vid.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen className="absolute inset-0 w-full h-full" />
                    </div>
                  ) : (
                    <Link href={`/videos/${vid.id}`}>
                      <div className="relative aspect-video rounded-xl overflow-hidden bg-muted group cursor-pointer">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Play className="h-12 w-12 text-muted-foreground/30 group-hover:text-primary transition-colors" />
                        </div>
                      </div>
                    </Link>
                  )}
                  <p className="text-sm font-medium mt-2 truncate">{vid.title}</p>
                  <p className="text-xs text-muted-foreground">{vid.genre || "Music Video"}</p>
                </div>
              );
            })}
            {/* Persistent add-video entry point — previously this only
                appeared when there were ZERO videos at all, so once a
                primary video existed there was no visible way to add more
                without knowing to open the separate Edit Profile dialog
                (Gordon 2026-09-20: "can't add videos???"). */}
            {canManage && (
              !artist.videoUrl && (!Array.isArray(artist.videos) || artist.videos.filter(Boolean).length === 0) && videos.length === 0 ? (
                <button
                  type="button"
                  onClick={onOpenEditProfile}
                  className="flex flex-col items-center justify-center py-12 text-center gap-3 border-2 border-dashed border-border/40 rounded-xl hover:border-primary/50 hover:bg-primary/5 transition-colors"
                >
                  <Video className="h-10 w-10 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">{t('noVideos')}</p>
                  <p className="text-xs text-primary font-medium">+ Add a video</p>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onOpenEditProfile}
                  className="flex flex-col items-center justify-center aspect-video border-2 border-dashed border-border/40 rounded-xl hover:border-primary/50 hover:bg-primary/5 transition-colors gap-2"
                >
                  <PlusCircle className="h-8 w-8 text-muted-foreground/40" />
                  <p className="text-sm text-primary font-medium">Add another video</p>
                </button>
              )
            )}
          </div>
        </TabsContent>
      )}

      {/* Gallery — mixed images/GIFs/videos, artist.images[] holds every kind */}
      {artist.images && artist.images.filter(Boolean).length > 0 && (
        <TabsContent value="gallery" className="mt-2">
          <MediaGalleryGrid items={artist.images} altPrefix={artist.name} />
        </TabsContent>
      )}

      {/* Members */}
      {artist.members && artist.members.length > 0 && (
        <TabsContent value="members" className="mt-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {artist.members.map((member: any, i: number) => {
              const card = (
                <Card className="bg-card/50 border-border/50 hover:border-primary/30 transition-all hover:shadow-lg">
                  <CardContent className="p-4 text-center">
                    <Avatar className="h-16 w-16 mx-auto mb-2 group-hover:ring-2 ring-primary/50 transition-all">
                      <AvatarFallback className="text-lg bg-primary/10" name={member.name}>{getInitials(member.name)}</AvatarFallback>
                    </Avatar>
                    <p className="font-medium text-sm group-hover:text-primary transition-colors">{member.name}</p>
                    {member.role && <p className="text-xs text-muted-foreground mt-0.5">{member.role}</p>}
                  </CardContent>
                </Card>
              );
              // Link only to an artist page that exists (member.slug). A
              // name-derived slug 404'd for members without one, and an
              // unclaimed placeholder account has no public profile yet.
              return member.slug
                ? <Link key={i} href={`/artists/${member.slug}`} className="group">{card}</Link>
                : <div key={i} className="group">{card}</div>;
            })}
          </div>
        </TabsContent>
      )}

      {/* News */}
      {news.length > 0 && (
        <TabsContent value="news" className="mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {news.map((article: any) => (
              <Link key={article.id} href={newsHref(article)} className="group">
                <Card className="overflow-hidden border-border/50 hover:border-primary/30 transition-all hover:shadow-lg">
                  <div className="relative aspect-video bg-muted">
                    {(article.featuredImageUrl || article.imageUrl) ? (
                      <Image src={article.featuredImageUrl || article.imageUrl} alt={article.title} fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center"><Newspaper className="h-8 w-8 text-muted-foreground/20" /></div>
                    )}
                  </div>
                  <CardContent className="p-3">
                    <p className="text-sm font-medium line-clamp-2">{article.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{article.category || "News"}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </TabsContent>
      )}

      {/* Shop */}
      {(shopItems.length > 0 || canManage) && (
        <TabsContent value="shop" className="mt-2">
          {shopItems.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">{t('merchEmpty')}</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {shopItems.map((shopItem: any) => (
              <Link key={shopItem.id} href={`/marketplace/${shopItem.id}`} className="group">
                <Card className="overflow-hidden border-border/50 hover:border-primary/30 transition-all hover:shadow-lg">
                  <div className="relative aspect-square bg-muted">
                    {(shopItem.media?.[0]?.url || shopItem.media?.[0] || shopItem.imageUrl) ? (
                      <Image
                        src={typeof shopItem.media?.[0] === "string" ? shopItem.media[0] : shopItem.media?.[0]?.url || shopItem.imageUrl}
                        alt={shopItem.title} fill
                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center"><ShoppingBag className="h-8 w-8 text-muted-foreground/20" /></div>
                    )}
                  </div>
                  <CardContent className="p-2.5">
                    <p className="text-xs font-medium truncate">{shopItem.title}</p>
                    {shopItem.price && <p className="text-xs font-bold text-primary mt-0.5">{shopItem.currency === "SEK" ? `${shopItem.price} kr` : `€${shopItem.price}`}</p>}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </TabsContent>
      )}

      {/* Social embeds */}
      {linkedAccounts.length > 0 && (
        <TabsContent value="social" className="mt-2">
          <SocialEmbeds links={linkedAccounts} name={artist.name} />
        </TabsContent>
      )}

      {/* Web Info */}
      <TabsContent value="web" className="mt-2">
        <WebResultsSection
          query={artist.name}
          entityType="artist"
          entityName={artist.name}
          entityId={artist.id}
          entityCollection="artists"
          canManage={isAdmin || isSuperAdmin || (!!currentUserId && [(artist as any).userId, (artist as any).createdByUid, (artist as any).createdBy].includes(currentUserId))}
        />
      </TabsContent>
    </Tabs>
  );
}
